import { Link } from 'react-router-dom';
import type { ReservationPolicy } from '@wineryxplorer/shared';
import { ImgWithSkeleton } from '../ui/ImgWithSkeleton';
import { ReservationChip } from './ReservationChip';
import { FavoriteButton } from '../FavoriteButton';

export interface TastingCardData {
  id: string;
  name: string;
  price: { amount: number; currency: string };
  duration: number | null;
  requiresReservation: boolean;
  wineClubOnly: boolean;
  heroImage: { cloudinaryUrl: string; cloudinaryPublicId: string } | null;
}

interface TastingCardProps {
  tasting: TastingCardData;
  venueHeroUrl: string | null;
  venuePolicy: ReservationPolicy | null;
}

const formatDuration = (mins: number | null) => {
  if (mins === null) return null;
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

const formatPrice = (amount: number, wineClubOnly: boolean) => {
  if (wineClubOnly && amount === 0) return 'Members';
  return `$${amount}`;
};

export const TastingCard = ({ tasting, venueHeroUrl, venuePolicy }: TastingCardProps) => {
  const heroUrl = tasting.heroImage?.cloudinaryUrl ?? venueHeroUrl ?? null;
  const duration = formatDuration(tasting.duration);

  return (
    <Link
      to={`/tasting-experiences/${tasting.id}`}
      className="group block overflow-hidden rounded-lg bg-gray-50 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-stretch h-40 sm:h-44">
        <div className="flex-shrink-0 w-[42%] sm:w-[40%] overflow-hidden bg-gray-100 relative">
          {heroUrl ? (
            <ImgWithSkeleton
              src={heroUrl}
              alt={tasting.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              skeletonClassName=""
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
              </svg>
            </div>
          )}
          <div
            className="absolute top-2 right-2 z-10"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          >
            <FavoriteButton entityType="tastingExperience" entityId={tasting.id} className="bg-white/80 backdrop-blur-sm" />
          </div>
        </div>

        <div className="flex-1 min-w-0 flex gap-3 p-3 sm:p-4">
          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <h3 className="font-serif text-base sm:text-xl font-normal leading-tight text-gray-900 group-hover:text-gray-700 transition-colors line-clamp-2">
              {tasting.name}
            </h3>

            {(duration || tasting.wineClubOnly) && (
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                {duration && <span className="font-sans tracking-wide">{duration}</span>}
                {tasting.wineClubOnly && (
                  <>
                    {duration && <span className="text-gray-300">·</span>}
                    <span className="font-sans tracking-widest uppercase text-[0.65rem] text-tertiary-700">
                      Wine Club Only
                    </span>
                  </>
                )}
              </div>
            )}

            <div className="mt-3">
              <ReservationChip
                requiresReservation={tasting.requiresReservation}
                venuePolicy={venuePolicy}
              />
            </div>
          </div>

          {/* Right rail — price, sm+ */}
          <div className="hidden sm:flex flex-shrink-0 flex-col items-end justify-center text-right gap-1 min-w-[4.5rem]">
            <span className="font-serif text-2xl font-normal leading-none text-gray-900">
              {formatPrice(tasting.price.amount, tasting.wineClubOnly)}
            </span>
            {tasting.price.amount > 0 && (
              <span className="text-[0.6rem] font-sans tracking-widest uppercase text-gray-500">
                per guest
              </span>
            )}
          </div>

          {/* Mobile price — inline */}
          <div className="sm:hidden flex-shrink-0 flex flex-col items-end justify-center">
            <span className="font-serif text-xl font-normal text-gray-900">
              {formatPrice(tasting.price.amount, tasting.wineClubOnly)}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};
